import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import type { Highlightable } from '@angular/cdk/a11y';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Injector,
    computed,
    contentChild,
    contentChildren,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { v4 as uuidv4 } from 'uuid';
import { CmdkService } from './cmdk.service';
import { QpxCmdkEmpty } from './empty';
import { QpxCmdkGroup } from './group';
import { QpxCmdkItem } from './item';
import { QpxCmdkList } from './list';
import { QpxCmdkLoader } from './loader';
import { QpxCmdkSeparator } from './separator';

type CmdkFilter = (value: string, search: string) => boolean;
type CmdkKeyManagerItem = Omit<QpxCmdkItem, 'disabled'> & Highlightable;

const GROUP_SELECTOR = 'qpx-cmdk-group';
const GROUP_HEADING_SELECTOR = '.qpx-cmdk-group-label';

/**
 * Command-menu primitive inspired by ngxpert/cmdk:
 * https://github.com/ngxpert/cmdk/blob/main/README.md
 */
@Component({
    selector: 'qpx-cmdk-command',
    template: '<ng-content />',
    providers: [CmdkService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'qpx-cmdk-command',
        '[attr.aria-label]': 'ariaLabel()',
        '[id]': 'panelId',
        '(keydown)': 'onKeyDown($event)',
    },
    exportAs: 'qpxCmdkCommand',
})
export class QpxCmdkCommand implements AfterViewInit, OnDestroy {
    //#region Dependency injection
    private readonly cmdkService = inject(CmdkService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    //#endregion

    //#region Inputs
    public readonly ariaLabel = input<string | undefined>();
    public readonly value = input<string | undefined>();
    public readonly loading = input(false);
    public readonly filter = input<CmdkFilter | null>((value, search) =>
        value.toLowerCase().includes(search.toLowerCase())
    );
    public readonly loop = input(false);
    //#endregion

    //#region Outputs
    public readonly valueChanged = output<string | undefined>();
    //#endregion

    //#region Queries
    private readonly items = contentChildren(QpxCmdkItem, {
        descendants: true,
    });
    private readonly groups = contentChildren(QpxCmdkGroup, {
        descendants: true,
    });
    private readonly lists = contentChildren(QpxCmdkList, {
        descendants: true,
    });
    private readonly separators = contentChildren(QpxCmdkSeparator, {
        descendants: true,
    });
    private readonly empty = contentChild(QpxCmdkEmpty);
    private readonly loader = contentChild(QpxCmdkLoader);
    //#endregion

    //#region State
    public readonly panelId = `cmdk-command-${uuidv4()}`;
    private readonly search = signal('');
    public readonly activeItemId = signal<string | null>(null);
    public readonly controlledListIds = computed(() => {
        const listIds = this.lists().map((list) => list.listId);
        return listIds.length > 0 ? listIds.join(' ') : null;
    });
    private keyManager:
        | ActiveDescendantKeyManager<CmdkKeyManagerItem>
        | undefined;
    //#endregion

    //#region Derived state
    private readonly filteredItems = computed(() =>
        this.items().filter((item) => item.filtered())
    );
    private readonly keyManagerItems = computed(
        () => [...this.items()] as unknown as CmdkKeyManagerItem[]
    );
    private readonly filteredGroups = computed(() =>
        this.groups().filter((group) => group.filtered())
    );
    private readonly filteredLists = computed(() =>
        this.lists().filter((list) => list.filtered())
    );
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        this.cmdkService.search$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((search) => this.handleSearch(search));

        this.cmdkService.itemClicked$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.setValue(value, true));

        this.cmdkService.itemValueChanged$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshVisibleState());

        effect(() => this.loader()?.show(this.loading()));
        effect(() => this.keyManager?.withWrap(this.loop()));
        effect(() => {
            this.items();
            this.refreshVisibleState();
        });
        effect(() => this.setValue(this.value()));
    }

    public ngAfterViewInit(): void {
        this.keyManager = new ActiveDescendantKeyManager<CmdkKeyManagerItem>(
            this.keyManagerItems,
            this.injector
        )
            .withWrap(this.loop())
            .withPageUpDown()
            .skipPredicate((item) => item.isDisabled() || !item.filtered());

        this.keyManager.change
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.handleActiveItemChange());

        this.refreshVisibleState();
    }

    public ngOnDestroy(): void {
        this.keyManager?.destroy();
    }
    //#endregion

    //#region Template methods
    protected onKeyDown(event: KeyboardEvent): void {
        if (!this.keyManager) return;

        if (
            event.key === 'Enter' &&
            this.keyManager.activeItem &&
            this.filteredItems().length > 0
        ) {
            const activeItem = this.keyManager.activeItem;
            this.valueChanged.emit(activeItem.resolvedValue());
            activeItem.selected.emit();
            return;
        }

        this.keyManager.onKeydown(event);
    }
    //#endregion

    //#region Private methods
    private handleSearch(search: string): void {
        this.search.set(search);

        this.applyFilter(search);

        this.refreshVisibleState();
        this.makeFirstItemActive();
    }

    private refreshVisibleState(): void {
        queueMicrotask(() => {
            this.empty()?.show(this.filteredItems().length === 0);

            for (const group of this.groups()) {
                group.setShowGroup(group.filteredItems().length > 0);
            }

            for (const separator of this.separators()) {
                separator.setShowSeparator(!(this.filter() && this.search()));
            }

            this.applyFilter(this.search());

            if (this.value()) {
                this.setValue(this.value());
            } else {
                this.makeFirstItemActive();
            }
        });
    }

    private applyFilter(search: string): void {
        const filter = this.filter();
        for (const item of this.items()) {
            item.setFiltered(
                filter ? filter(item.resolvedValue(), search) : true
            );
        }
    }

    private handleActiveItemChange(): void {
        const activeItem = this.keyManager?.activeItem;
        this.activeItemId.set(activeItem?.itemId ?? null);
        if (!activeItem) return;

        this.setValue(activeItem.resolvedValue(), true);
        this.setActiveGroupForActiveItem(activeItem.itemId);
    }

    private makeFirstItemActive(): void {
        queueMicrotask(() => {
            const firstItem = this.filteredItems()[0];
            if (firstItem && this.keyManager) {
                this.keyManager.setFirstItemActive();
                return;
            }

            this.keyManager?.setActiveItem(-1);
            for (const group of this.groups()) group.setActive(false);
            for (const list of this.lists()) list.setActive(false);
            this.activeItemId.set(null);
            this.valueChanged.emit(undefined);
        });
    }

    private setActiveGroupForActiveItem(nextActiveItemId: string): void {
        for (const group of this.filteredGroups()) {
            group.setActive(
                group
                    .filteredItems()
                    .some((item) => item.itemId === nextActiveItemId)
            );
        }

        for (const list of this.filteredLists()) {
            list.setActive(
                list
                    .filteredItems()
                    .some((item) => item.itemId === nextActiveItemId)
            );
        }
    }

    private setValue(value: string | undefined, emit = false): void {
        if (value === undefined || !this.keyManager) return;

        const valueItem = this.keyManagerItems().find(
            (item) => item.resolvedValue() === value
        );
        if (!valueItem) return;

        if (this.keyManager.activeItem !== valueItem) {
            queueMicrotask(() => this.keyManager?.setActiveItem(valueItem));
        }

        queueMicrotask(() => this.scrollActiveIntoView());

        if (emit) {
            this.valueChanged.emit(value);
        }
    }

    private scrollActiveIntoView(): void {
        const nativeElement = this.keyManager?.activeItem?.nativeElement();
        if (!nativeElement) return;

        if (nativeElement.parentElement?.firstChild === nativeElement) {
            nativeElement
                .closest(GROUP_SELECTOR)
                ?.querySelector(GROUP_HEADING_SELECTOR)
                ?.scrollIntoView({ block: 'nearest' });
        }

        nativeElement.scrollIntoView({ block: 'nearest' });
    }
    //#endregion
}
